import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, ApiError, getToken } from '../api/client';
import { PhotosEdl, type PhotoPrise } from '../components/PhotosEdl';
import { dataUrlEnBlob, envoyerPhotos } from '../lib/photos';
import { effacerBrouillon, enregistrerBrouillon, lireBrouillon, mettreEnFile } from '../lib/offline';
import { Bandeau, Carte, Champ, Squelette } from '../components/ui';
import { SchemaVoiture, Signature, type Dommage } from '../components/SchemaVoiture';
import { CARBURANT_LABEL, dateHeureFr, montant, nomClient } from '../lib/format';
import type { Client, Contrat, LigneSupplement, Vehicule } from '../api/types';

const ACCESSOIRES: [string, string][] = [
  ['cle', 'Clé'], ['carteGrise', 'Carte grise'], ['assurance', 'Assurance'], ['vignette', 'Vignette'],
  ['controleTechnique', 'Contrôle technique'], ['giletTriangle', 'Gilet & triangle'],
  ['crick', 'Crick'], ['roueSecours', 'Roue de secours'],
];
const TYPES = ['rayure', 'choc', 'eclat', 'manquant'];

/**
 * Les memes controles que sur leur contrat papier : carburant en QUARTS,
 * proprete notee 10/10 · 5/10 · 0/10, et les huit accessoires — ni plus, ni moins.
 */
/** Les quatre suppléments que le contrat impose ; cote serveur : CODES_ATTENDUS. */
const LIBELLE_SUPPLEMENT: Record<string, string> = {
  retard: 'Retard (article 02)',
  carburant_manquant: 'Carburant manquant (article 04)',
  km_supplementaire: 'Kilomètre supplémentaire (article 05)',
  nettoyage: 'Lavage partiel (article 03)',
};

/** Ce qui doit survivre a un rechargement : tout ce que l'agent a saisi dehors. */
interface SaisieEnCours {
  etape: number; km: string; quarts: number; propInt: number; propExt: number;
  acc: Record<string, boolean>; dommages: Dommage[]; photos: PhotoPrise[];
  observations: string; sigClient: string; sigAgent: string;
}

export default function EtatDesLieux() {
  const { id, sens } = useParams();
  const nav = useNavigate();
  const retour = sens === 'retour';

  const [contrat, setContrat] = useState<Contrat | null>(null);
  const [etape, setEtape] = useState(1);
  const [km, setKm] = useState('');
  const [quarts, setQuarts] = useState(4);
  const [propInt, setPropInt] = useState(10);
  const [propExt, setPropExt] = useState(10);
  const [acc, setAcc] = useState<Record<string, boolean>>(Object.fromEntries(ACCESSOIRES.map(([k]) => [k, true])));
  const [dommages, setDommages] = useState<Dommage[]>([]);
  const [photos, setPhotos] = useState<PhotoPrise[]>([]);
  const [horsLigne, setHorsLigne] = useState(false);
  const [nouveau, setNouveau] = useState<Dommage | null>(null);
  const [observations, setObservations] = useState('');
  const [sigClient, setSigClient] = useState('');
  const [sigAgent, setSigAgent] = useState('');
  const [resultat, setResultat] = useState<{ supplements: LigneSupplement[]; nouveauxDommages: Dommage[]; codesManquants?: string[] } | null>(null);
  const [erreur, setErreur] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [progres, setProgres] = useState('');
  const [sansPhotoAssume, setSansPhotoAssume] = useState(false);
  const [brouillonPropose, setBrouillonPropose] = useState<SaisieEnCours | null>(null);
  const [brouillonCharge, setBrouillonCharge] = useState(false);
  const cleBrouillon = `edl:${id}:${sens}`;

  // Un brouillon existant est PROPOSE, jamais applique d'office : l'agent doit
  // savoir qu'il reprend une saisie, et pouvoir repartir de zero s'il prefere.
  useEffect(() => {
    lireBrouillon<SaisieEnCours>(cleBrouillon)
      .then((b) => { if (b?.donnees) setBrouillonPropose(b.donnees); })
      .catch(() => {})
      .finally(() => setBrouillonCharge(true));
  }, [cleBrouillon]);

  // Ecriture continue, une fois le brouillon eventuel traite.
  useEffect(() => {
    if (!brouillonCharge || brouillonPropose || etape >= 9) return;
    const vide = !km && dommages.length === 0 && photos.length === 0 && !observations && !sigClient && !sigAgent;
    if (vide && etape === 1) return;
    const t = setTimeout(() => {
      void enregistrerBrouillon<SaisieEnCours>(cleBrouillon, {
        etape, km, quarts, propInt, propExt, acc, dommages, photos, observations, sigClient, sigAgent,
      }).catch(() => {});
    }, 400);
    return () => clearTimeout(t);
  }, [brouillonCharge, brouillonPropose, cleBrouillon, etape, km, quarts, propInt, propExt, acc, dommages, photos, observations, sigClient, sigAgent]);

  // Filet supplementaire : le navigateur previent avant de fermer l'onglet.
  useEffect(() => {
    if (etape >= 9) return;
    const rien = photos.length === 0 && dommages.length === 0 && !sigClient && !sigAgent;
    if (rien) return;
    const h = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [etape, photos.length, dommages.length, sigClient, sigAgent]);

  function reprendre(b: SaisieEnCours) {
    setEtape(b.etape); setKm(b.km); setQuarts(b.quarts);
    setPropInt(b.propInt); setPropExt(b.propExt); setAcc(b.acc);
    setDommages(b.dommages); setPhotos(b.photos); setObservations(b.observations);
    setSigClient(b.sigClient); setSigAgent(b.sigAgent);
    setBrouillonPropose(null);
  }

  function repartirDeZero() {
    void effacerBrouillon(cleBrouillon).catch(() => {});
    setBrouillonPropose(null);
  }

  useEffect(() => {
    api.get<{ contrat: Contrat }>(`/contrats/${id}`).then((d) => {
      setContrat(d.contrat);
      const v = d.contrat.vehicle as Vehicule;
      setKm(String(retour ? (d.contrat.remise?.km ?? v.km) : v.km));
    });
  }, [id, retour]);

  if (!contrat) return <Squelette lignes={6} />;
  const v = contrat.vehicle as Vehicule;
  const c = contrat.customer as Client;
  const depart = contrat.remise;

  async function valider() {
    setErreur(''); setEnvoi(true);
    try {
      const base = {
        dateHeure: new Date().toISOString(),
        km: Number(km), carburantQuarts: quarts,
        propreteInterieur: propInt, propreteExterieur: propExt,
        accessoires: acc,
        dommages: dommages.map(({ type, zone, x, y, commentaire }) => ({ type, zone, x, y, commentaire })),
        observations, signatureClient: sigClient, signatureAgent: sigAgent,
      };

      // Hors ligne : tout part dans la file, photos comprises, et repart seul au retour du reseau.
      if (!navigator.onLine) {
        await mettreEnFile(
          `/contrats/${id}/${retour ? 'restitution' : 'remise'}`,
          { ...base, photos: photos.map((p) => p.dataUrl) },
          `Etat des lieux ${retour ? 'retour' : 'depart'} — ${contrat!.numero}`,
        );
        await effacerBrouillon(cleBrouillon).catch(() => {});
        setHorsLigne(true); setEtape(9);
        return;
      }

      const urls = await envoyerPhotos(
        await Promise.all(photos.map((p) => dataUrlEnBlob(p.dataUrl))),
        getToken(),
        (faites, total) => setProgres(`Envoi des photos ${faites}/${total}…`),
      );
      setProgres('');
      const corps = { ...base, photos: urls };
      if (retour) {
        const r = await api.post<{ supplements: LigneSupplement[]; nouveauxDommages: Dommage[]; codesManquants?: string[] }>(`/contrats/${id}/restitution`, corps);
        await effacerBrouillon(cleBrouillon).catch(() => {});
        setResultat(r); setEtape(9);
      } else {
        await api.post(`/contrats/${id}/remise`, corps);
        await effacerBrouillon(cleBrouillon).catch(() => {});
        nav(`/reservations/${typeof contrat!.reservation === 'string' ? contrat!.reservation : contrat!.reservation._id}`);
      }
    } catch (e) {
      setErreur(e instanceof ApiError ? e.erreur : 'Enregistrement impossible');
    } finally { setEnvoi(false); }
  }

  const totalExtras = resultat?.supplements.reduce((a, l) => a + l.totalMinor, 0) ?? 0;

  if (etape === 9 && horsLigne) {
    return (
      <>
        <div className="entete"><div className="titres"><h1>Enregistré hors ligne</h1><p>{contrat.numero} · {v.marque} {v.modele}</p></div></div>
        <Bandeau type="warn" titre="Mis en file d'attente">
          Vous n'avez pas de reseau. L'état des lieux et ses {photos.length} photo(s) sont conserves sur ce téléphone
          et partiront tout seuls des que la connexion revient. Vous pouvez continuer a travailler.
        </Bandeau>
        <div className="actions" style={{ marginTop: 16 }}>
          <button className="btn btn-p btn-l" onClick={() => nav('/')}>Revenir a mes tâches</button>
        </div>
      </>
    );
  }

  if (etape === 9 && resultat) {
    return (
      <>
        <div className="entete"><div className="titres"><h1>Retour enregistré</h1><p>{contrat.numero} · {v.marque} {v.modele}</p></div></div>
        {resultat.nouveauxDommages.length > 0 && (
          <div style={{ marginBottom: 14 }}>
            <Bandeau type="ko" titre={`${resultat.nouveauxDommages.length} nouveau(x) dommage(s)`}>
              Constatés au retour, absents du départ. C'est la comparaison qui évite le litige.
            </Bandeau>
          </div>
        )}
        <Carte titre="Suppléments a facturer" pad={false}>
          {resultat.codesManquants && resultat.codesManquants.length > 0 ? (
            /*
             * Le serveur signale que des suppléments du contrat ne sont pas au
             * catalogue. Sans eux, le calcul est partiel — et on est exactement
             * a l'endroit ou se decide la restitution de la caution. Dire
             * « conforme » ici serait un mensonge couteux.
             */
            <div className="carte-p">
              <Bandeau type="ko" titre="Retour NON chiffrable — ne restituez pas la caution">
                Ces suppléments prévus par votre contrat sont absents du catalogue, donc jamais calculés :
                <ul style={{ margin: '8px 0 0 18px' }}>
                  {resultat.codesManquants.map((c) => <li key={c}>{LIBELLE_SUPPLEMENT[c] ?? c}</li>)}
                </ul>
                Un gérant doit les renseigner dans <b>Paramètres › Suppléments</b> avant de solder ce retour.
                {totalExtras > 0 && <> Le total affiché ci-dessous est <b>incomplet</b>.</>}
              </Bandeau>
            </div>
          ) : null}
          {resultat.supplements.length === 0 && !(resultat.codesManquants?.length) ? (
            <div className="carte-p"><Bandeau type="ok" titre="Aucun supplément">Véhicule rendu conforme.</Bandeau></div>
          ) : (
            <table className="tbl">
              <thead><tr><th>Supplément</th><th>Calcul</th><th className="droite">Montant</th></tr></thead>
              <tbody>
                {resultat.supplements.map((l) => (
                  <tr key={l.code}>
                    <td><b>{l.libelle}</b></td>
                    <td className="mini sourdine">{l.formule}</td>
                    <td className="droite montant">{montant(l.totalMinor)}</td>
                  </tr>
                ))}
                <tr><td colSpan={2}><b>Total des suppléments</b></td><td className="droite montant"><b>{montant(totalExtras)}</b></td></tr>
              </tbody>
            </table>
          )}
        </Carte>
        <div className="actions" style={{ marginTop: 16 }}>
          <button className="btn btn-p btn-l" onClick={() => nav(`/reservations/${typeof contrat.reservation === 'string' ? contrat.reservation : contrat.reservation._id}`)}>
            Voir le dossier
          </button>
          <button className="btn" onClick={() => nav(`/caisse?contrat=${contrat._id}`)}>Encaisser le solde</button>
        </div>
      </>
    );
  }

  if (brouillonPropose) {
    const b = brouillonPropose;
    return (
      <>
        <div className="entete">
          <div className="titres">
            <h1>État des lieux — {retour ? 'retour' : 'départ'}</h1>
            <p>{contrat.numero} · {v.marque} {v.modele} · {v.immatriculation} — {nomClient(c)}</p>
          </div>
        </div>
        <Bandeau type="warn" titre="Une saisie est déjà en cours sur cet appareil">
          Vous étiez à l'<b>étape {b.etape} sur 5</b>
          {b.photos.length > 0 && <>, avec <b>{b.photos.length} photo(s)</b></>}
          {b.dommages.length > 0 && <>, <b>{b.dommages.length} dommage(s)</b> relevé(s)</>}
          {(b.sigClient || b.sigAgent) && <>, et <b>{[b.sigClient && 'la signature du client', b.sigAgent && "celle de l'agent"].filter(Boolean).join(' et ')}</b></>}.
          <div className="actions" style={{ marginTop: 14 }}>
            <button className="btn btn-p btn-l" onClick={() => reprendre(b)}>Reprendre où j'en étais</button>
            <button className="btn" onClick={repartirDeZero}>Tout recommencer</button>
          </div>
        </Bandeau>
      </>
    );
  }

  return (
    <>
      <div className="entete">
        <div className="titres">
          <h1>État des lieux — {retour ? 'retour' : 'départ'}</h1>
          <p>{contrat.numero} · {v.marque} {v.modele} · {v.immatriculation} — {nomClient(c)}</p>
        </div>
        <div className="mini sourdine">Étape {etape} sur 5</div>
      </div>

      {retour && depart && (
        <div style={{ marginBottom: 14 }}>
          <Bandeau type="info" titre="Au départ">
            {dateHeureFr(depart.dateHeure)} · {depart.km.toLocaleString('fr-FR')} km · carburant {CARBURANT_LABEL[depart.carburantQuarts]} ·
            propreté {depart.propreteInterieur}/10 int. {depart.propreteExterieur}/10 ext. · {depart.dommages.length} dommage(s) constaté(s)
          </Bandeau>
        </div>
      )}
      {erreur && <div style={{ marginBottom: 14 }}><Bandeau type="ko">{erreur}</Bandeau></div>}

      <div className="grille g2" style={{ alignItems: 'start' }}>
        <div style={{ display: 'grid', gap: 14 }}>
          {etape === 1 && (
            <Carte titre="Photos">
              <PhotosEdl photos={photos} onChange={setPhotos} />
            </Carte>
          )}

          {etape === 2 && (
            <Carte titre="Dommages">
              <SchemaVoiture
                dommages={dommages}
                anciens={retour ? (depart?.dommages ?? []) : []}
                onAjouter={(x, y) => setNouveau({ type: 'rayure', zone: y < .3 ? 'avant' : y > .75 ? 'arriere' : x < .5 ? 'cote_gauche' : 'cote_droit', x, y })}
              />
              {retour && (depart?.dommages.length ?? 0) > 0 && (
                <p className="mini sourdine" style={{ marginTop: 8 }}>Les points gris sont ceux constatés au départ.</p>
              )}
              {nouveau && (
                <div style={{ marginTop: 12, display: 'grid', gap: 10 }}>
                  <Champ label="Type de dommage">
                    <div className="choix">
                      {TYPES.map((t) => (
                        <button key={t} type="button" aria-pressed={nouveau.type === t} onClick={() => setNouveau({ ...nouveau, type: t })}>{t}</button>
                      ))}
                    </div>
                  </Champ>
                  <Champ label="Commentaire">
                    <input value={nouveau.commentaire ?? ''} onChange={(e) => setNouveau({ ...nouveau, commentaire: e.target.value })} placeholder="Rayure portière avant droite" />
                  </Champ>
                  <div className="actions">
                    <button className="btn" onClick={() => setNouveau(null)}>Annuler</button>
                    <button className="btn btn-p" onClick={() => { setDommages([...dommages, nouveau]); setNouveau(null); }}>Ajouter</button>
                  </div>
                </div>
              )}
              {dommages.length > 0 && (
                <ul style={{ marginTop: 12, paddingLeft: 18 }} className="mini">
                  {dommages.map((d, i) => (
                    <li key={i}>{i + 1}. {d.type} — {d.zone.replace('_', ' ')} {d.commentaire && `· ${d.commentaire}`}
                      <button className="btn btn-s btn-f" onClick={() => setDommages(dommages.filter((_, j) => j !== i))}>retirer</button>
                    </li>
                  ))}
                </ul>
              )}
            </Carte>
          )}

          {etape === 3 && (
            <Carte titre="Carburant, kilométrage et propreté">
              <div style={{ display: 'grid', gap: 16 }}>
                <Champ label="Carburant" aide="Les quatre cases de votre contrat papier (article 04)">
                  <div className="choix">
                    {[1, 2, 3, 4].map((q) => (
                      <button key={q} type="button" aria-pressed={quarts === q} onClick={() => setQuarts(q)}>{CARBURANT_LABEL[q]}</button>
                    ))}
                  </div>
                </Champ>
                <Champ label="Kilométrage au compteur"
                       aide={retour && depart ? `Depart : ${depart.km.toLocaleString('fr-FR')} km — soit ${Math.max(0, Number(km) - depart.km).toLocaleString('fr-FR')} km parcourus` : undefined}>
                  <input type="number" inputMode="numeric" value={km} onChange={(e) => setKm(e.target.value)} />
                </Champ>
                <Champ label="Propreté intérieure">
                  <div className="choix">{[10, 5, 0].map((p) => (
                    <button key={p} type="button" aria-pressed={propInt === p} onClick={() => setPropInt(p)}>{p}/10</button>))}
                  </div>
                </Champ>
                <Champ label="Propreté extérieure">
                  <div className="choix">{[10, 5, 0].map((p) => (
                    <button key={p} type="button" aria-pressed={propExt === p} onClick={() => setPropExt(p)}>{p}/10</button>))}
                  </div>
                </Champ>
              </div>
            </Carte>
          )}

          {etape === 4 && (
            <Carte titre="Accessoires">
              <div className="accessoires">
                {ACCESSOIRES.map(([k, l]) => (
                  <label key={k}>
                    <input type="checkbox" checked={acc[k] ?? false} onChange={(e) => setAcc({ ...acc, [k]: e.target.checked })} />
                    {l}
                  </label>
                ))}
              </div>
              <div style={{ marginTop: 14 }}>
                <Champ label="Observations"><textarea value={observations} onChange={(e) => setObservations(e.target.value)} /></Champ>
              </div>
            </Carte>
          )}

          {etape === 5 && (
            <Carte titre="Signatures">
              <div style={{ display: 'grid', gap: 16 }}>
                <Signature label="Signature du client — précédé de « Lu et approuvé »" valeur={sigClient} onChange={setSigClient} />
                <Signature label="Signature du directeur / de l'agent" valeur={sigAgent} onChange={setSigAgent} />
              </div>
              {/*
                * L'etat des lieux photographique et les deux signatures sont,
                * avec le journal, ce qui tient lieu de preuve. Un etat des lieux
                * sans signature n'engage personne : il ne doit pas pouvoir etre
                * valide par distraction.
                */}
              {(!sigClient || !sigAgent) && (
                <div style={{ marginTop: 14 }}>
                  <Bandeau type="ko" titre="Signatures manquantes">
                    {!sigClient && !sigAgent
                      ? "Les deux signatures manquent."
                      : !sigClient ? "La signature du client manque." : "Votre signature manque."}
                    {' '}Sans elles, ce document n'engage personne en cas de litige.
                  </Bandeau>
                </div>
              )}

              {photos.length === 0 && (
                <div style={{ marginTop: 14 }}>
                  <Bandeau type="warn" titre="Aucune photo">
                    L'état des lieux photographique est votre meilleure protection en cas de
                    contestation sur un dommage. Prenez au moins les quatre côtés à l'étape 4.
                    <label style={{ display: 'flex', gap: 9, alignItems: 'center', marginTop: 10 }}>
                      <input type="checkbox" checked={sansPhotoAssume}
                             onChange={(e) => setSansPhotoAssume(e.target.checked)} />
                      Je valide sans photo, en connaissance de cause
                    </label>
                  </Bandeau>
                </div>
              )}

              <div style={{ marginTop: 14 }}>
                <Bandeau type="warn" titre="Une fois enregistré, vous ne pourrez plus modifier">
                  Vérifiez avant de valider. En cas d'erreur, il faudra passer par une demande de correction.
                </Bandeau>
              </div>
            </Carte>
          )}

          <div className="actions" style={{ justifyContent: 'space-between' }}>
            <button className="btn" disabled={etape === 1} onClick={() => setEtape(etape - 1)}>Retour</button>
            {etape < 5
              ? <button className="btn btn-p btn-l" onClick={() => setEtape(etape + 1)}>Suivant</button>
              : <button className="btn btn-p btn-l"
                        disabled={envoi || !km || !sigClient || !sigAgent || (photos.length === 0 && !sansPhotoAssume)}
                        onClick={() => void valider()}>
                  {envoi ? (progres || 'Enregistrement…') : retour ? 'Valider le retour' : 'Valider le départ'}
                </button>}
          </div>
        </div>

        <Carte titre="Récapitulatif">
          <table className="tbl compact">
            <tbody>
              <tr><td>Carburant</td><td className="droite"><b>{CARBURANT_LABEL[quarts]}</b></td></tr>
              <tr><td>Kilométrage</td><td className="droite num">{Number(km || 0).toLocaleString('fr-FR')} km</td></tr>
              <tr><td>Propreté</td><td className="droite">{propInt}/10 int. · {propExt}/10 ext.</td></tr>
              <tr><td>Accessoires presents</td><td className="droite">{Object.values(acc).filter(Boolean).length} / 8</td></tr>
              <tr><td>Photos prises</td><td className="droite" style={{ color: photos.length >= 6 ? 'var(--succes)' : 'var(--attention)' }}>{photos.length} / 6</td></tr>
              <tr><td>Dommages marqués</td><td className="droite">{dommages.length}</td></tr>
              <tr><td>Signatures</td><td className="droite">{[sigClient && 'client', sigAgent && 'agent'].filter(Boolean).join(' + ') || '—'}</td></tr>
            </tbody>
          </table>
        </Carte>
      </div>
    </>
  );
}
